import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import IncomingCallModal from './IncomingCallModal';

const IncomingCallListener = () => {
  const { currentUser } = useAuth();
  const [incomingCall, setIncomingCall] = useState(null);
  const [callerInfo, setCallerInfo] = useState(null);

  useEffect(() => {
    if (!currentUser?.uid) return;

    // Listen for incoming video call notifications
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', currentUser.uid),
      where('type', '==', 'video_call_incoming'),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(q, 
      async (snapshot) => {
        // Get the most recent unread incoming call notification
        const notifications = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => {
            const aTime = a.createdAt?.toDate?.() || new Date(0);
            const bTime = b.createdAt?.toDate?.() || new Date(0);
            return bTime - aTime;
          });

        if (notifications.length > 0) {
          const latestNotification = notifications[0];
          
          // Check if call is still active
          if (latestNotification.data?.callId) {
            const callRef = doc(db, 'videoCalls', latestNotification.data.callId);
            const callSnap = await getDoc(callRef);
            
            if (callSnap.exists()) {
              const callData = callSnap.data();
              // Only show if call is still active
              if (callData.status === 'active') {
                // Get caller info
                if (latestNotification.data?.callerId) {
                  const callerRef = doc(db, 'users', latestNotification.data.callerId);
                  const callerSnap = await getDoc(callerRef);
                  if (callerSnap.exists()) {
                    const callerData = callerSnap.data();
                    setCallerInfo({
                      name: callerData.displayName || callerData.name || 'ผู้ใช้',
                      ...callerData
                    });
                  }
                }
                
                setIncomingCall({
                  ...latestNotification,
                  callId: latestNotification.data.callId,
                  chatId: latestNotification.data.chatId,
                  productId: latestNotification.data.productId,
                  storeId: latestNotification.data.storeId
                });
              }
            }
          }
        } else {
          // No incoming calls
          setIncomingCall(null);
          setCallerInfo(null);
        }
      },
      (error) => {
        console.error('Error listening to incoming calls:', error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [currentUser?.uid]);

  const handleAccept = async () => {
    if (incomingCall) {
      // Mark notification as read
      try {
        const notificationRef = doc(db, 'notifications', incomingCall.id);
        await updateDoc(notificationRef, {
          read: true,
          readAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
      
      setIncomingCall(null);
      setCallerInfo(null);
    }
  };

  const handleReject = async () => {
    if (incomingCall) {
      // Mark notification as read
      try {
        const notificationRef = doc(db, 'notifications', incomingCall.id);
        await updateDoc(notificationRef, {
          read: true,
          readAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        
        // Optionally: Update call status to rejected
        if (incomingCall.callId) {
          const callRef = doc(db, 'videoCalls', incomingCall.callId);
          await updateDoc(callRef, {
            status: 'rejected',
            rejectedAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      } catch (error) {
        console.error('Error rejecting call:', error);
      }
      
      setIncomingCall(null);
      setCallerInfo(null);
    }
  };

  // Get product/store name from notification message
  const getProductName = () => {
    if (incomingCall?.message) {
      const match = incomingCall.message.match(/สำหรับสินค้า "([^"]+)"/);
      return match ? match[1] : null;
    }
    return null;
  };

  const getStoreName = () => {
    if (incomingCall?.message) {
      const match = incomingCall.message.match(/จากร้าน "([^"]+)"/);
      return match ? match[1] : null;
    }
    return null;
  };

  return (
    <IncomingCallModal
      isOpen={!!incomingCall}
      callerName={callerInfo?.name || 'ผู้ใช้'}
      productName={getProductName()}
      storeName={getStoreName()}
      callId={incomingCall?.callId}
      chatId={incomingCall?.chatId}
      productId={incomingCall?.productId}
      storeId={incomingCall?.storeId}
      onAccept={handleAccept}
      onReject={handleReject}
    />
  );
};

export default IncomingCallListener;


